import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

export interface RoastUpvoter {
  user_id: string;
  screen_name: string;
  avatar_url: string | null;
}

interface IGetRoastUpvotersQueryFnParams {
  supabase: SupabaseClient<Database>;
  roastId: string;
}

const getRoastUpvotersQueryFn = async ({
  supabase,
  roastId,
}: IGetRoastUpvotersQueryFnParams): Promise<RoastUpvoter[]> => {
  const { data: votes, error } = await supabase
    .from('roast_vote')
    .select('user_id')
    .eq('roast_id', roastId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!votes || votes.length === 0) return [];

  const userIds = Array.from(new Set(votes.map((v: { user_id: string }) => v.user_id)));

  const { data: users } = await supabase
    .from('user_profile_view')
    .select('id, screen_name, avatar_url')
    .in('id', userIds);

  if (!users) return [];

  const userMap = Object.fromEntries(
    (users as unknown as { id: string; screen_name: string; avatar_url: string | null }[]).map(
      (u) => [u.id, { screen_name: u.screen_name, avatar_url: u.avatar_url }]
    )
  );

  return userIds
    .filter((id) => userMap[id])
    .map((id) => ({
      user_id: id,
      screen_name: userMap[id].screen_name,
      avatar_url: userMap[id].avatar_url,
    }));
};

interface IGetRoastUpvotersQueryParams {
  roastId?: string | null;
  upvoteCount?: number;
}

export const useGetRoastUpvotersQuery = ({
  roastId,
  upvoteCount = 0,
}: IGetRoastUpvotersQueryParams) => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetRoastUpvoters, roastId],
    queryFn: () =>
      getRoastUpvotersQueryFn({
        supabase,
        roastId: roastId!,
      }),
    enabled: Boolean(roastId) && upvoteCount > 0,
  });

  return {
    data: data ?? [],
    isPending,
  };
};
